import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, AlertCircle } from 'lucide-react';
import { StockPost } from './StockSharingFeed';
import { authService } from '../lib/auth';

interface PostStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (post: Omit<StockPost, 'id' | 'timestamp' | 'status' | 'messages'>) => void;
  source: 'internal' | 'external'; // Where the post is coming from
  availableProperties?: Array<{ id: string; title: string; }>; // For Agent Hub
}

export function PostStockModal({ isOpen, onClose, onSubmit, source, availableProperties = [] }: PostStockModalProps) {
  const [type, setType] = useState<'BUY' | 'SELL'>('SELL');
  const [propertyTitle, setPropertyTitle] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [size, setSize] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!propertyTitle.trim()) newErrors.propertyTitle = 'Property title is required';
    if (!propertyType) newErrors.propertyType = 'Property type is required';
    if (!location.trim()) newErrors.location = 'Location is required';
    if (!price.trim()) newErrors.price = 'Price is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (description.length > 500) newErrors.description = 'Description must be under 500 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    onSubmit({
      type,
      propertyTitle,
      propertyType,
      location,
      price,
      bedrooms: bedrooms ? parseInt(bedrooms) : undefined,
      bathrooms: bathrooms ? parseInt(bathrooms) : undefined,
      size: size ? parseInt(size) : undefined,
      description,
      agentName: authService.getCurrentUser()?.name || authService.getCurrentUser()?.username?.split('@')[0] || 'Unknown Agent',
      agency: source === 'internal' ? 'Something Properties' : 'External Agency',
      source,
      internalPropertyId: selectedPropertyId || undefined,
      externalListingUrl: externalUrl || undefined
    });

    // Reset form
    setPropertyTitle('');
    setPropertyType('');
    setLocation('');
    setPrice('');
    setBedrooms('');
    setBathrooms('');
    setSize('');
    setDescription('');
    setSelectedPropertyId('');
    setExternalUrl('');
    setErrors({});
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#1a1a1a] border-b border-white/10 p-6 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-2xl font-bold">Post Stock</h2>
                  <p className="text-sm text-white/60 mt-1">Share property opportunities with fellow agents</p>
                </div>
                <button
                  onClick={handleClose}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Transaction Type */}
                <div>
                  <label className="block text-sm font-medium mb-3 text-white/90">
                    Transaction Type <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setType('BUY')}
                      className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                        type === 'BUY'
                          ? 'bg-blue-500/20 text-blue-400 border-2 border-blue-500'
                          : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      Looking to BUY
                    </button>
                    <button
                      type="button"
                      onClick={() => setType('SELL')}
                      className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                        type === 'SELL'
                          ? 'bg-green-500/20 text-green-400 border-2 border-green-500'
                          : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      Available to SELL
                    </button>
                  </div>
                </div>

                {/* Property Title */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-white/90">
                    Property Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={propertyTitle}
                    onChange={(e) => setPropertyTitle(e.target.value)}
                    placeholder="e.g., 2 Bed Apartment - Sandton"
                    className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:bg-white/10 transition-all ${
                      errors.propertyTitle ? 'border-red-500' : 'border-white/10 focus:border-[#FDB022]/50'
                    }`}
                  />
                  {errors.propertyTitle && (
                    <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.propertyTitle}
                    </p>
                  )}
                </div>

                {/* Property Type & Location */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white/90">
                      Property Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value)}
                      className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:bg-white/10 transition-all ${
                        errors.propertyType ? 'border-red-500' : 'border-white/10 focus:border-[#FDB022]/50'
                      }`}
                    >
                      <option value="">Select type</option>
                      <option value="Apartment">Apartment</option>
                      <option value="House">House</option>
                      <option value="Penthouse">Penthouse</option>
                      <option value="Townhouse">Townhouse</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Land">Land</option>
                      <option value="Farm">Farm</option>
                    </select>
                    {errors.propertyType && (
                      <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.propertyType}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-white/90">
                      Location <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="e.g., Sandton, Johannesburg"
                      className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:bg-white/10 transition-all ${
                        errors.location ? 'border-red-500' : 'border-white/10 focus:border-[#FDB022]/50'
                      }`}
                    />
                    {errors.location && (
                      <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.location}
                      </p>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-white/90">
                    {type === 'BUY' ? 'Budget Range' : 'Price'} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder={type === 'BUY' ? 'e.g., R 2,000,000 - R 3,000,000' : 'e.g., R 2,500,000'}
                    className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:bg-white/10 transition-all ${
                      errors.price ? 'border-red-500' : 'border-white/10 focus:border-[#FDB022]/50'
                    }`}
                  />
                  {errors.price && (
                    <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.price}
                    </p>
                  )}
                </div>

                {/* Optional Fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white/90">Bedrooms</label>
                    <input
                      type="number"
                      value={bedrooms}
                      onChange={(e) => setBedrooms(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#FDB022]/50 focus:bg-white/10 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-white/90">Bathrooms</label>
                    <input
                      type="number"
                      value={bathrooms}
                      onChange={(e) => setBathrooms(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#FDB022]/50 focus:bg-white/10 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-white/90">Size (m²)</label>
                    <input
                      type="number"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#FDB022]/50 focus:bg-white/10 transition-all"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-white/90">
                    Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide key details about the property or requirements..."
                    rows={4}
                    maxLength={500}
                    className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:bg-white/10 transition-all resize-none ${
                      errors.description ? 'border-red-500' : 'border-white/10 focus:border-[#FDB022]/50'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-2">
                    {errors.description ? (
                      <p className="text-sm text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    ) : (
                      <p className="text-sm text-white/50">
                        {description.length}/500 characters
                      </p>
                    )}
                  </div>
                </div>

                {/* Property Linking */}
                {source === 'internal' && type === 'SELL' && availableProperties.length > 0 && (
                  <div className="p-4 bg-[#FDB022]/10 border border-[#FDB022]/30 rounded-xl">
                    <label className="block text-sm font-medium mb-2 text-white/90">
                      Link to Catalog Property (Optional)
                    </label>
                    <select
                      value={selectedPropertyId}
                      onChange={(e) => setSelectedPropertyId(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#FDB022]/50 focus:bg-white/10 transition-all"
                    >
                      <option value="">Select a property from your listings</option>
                      {availableProperties.map((prop) => (
                        <option key={prop.id} value={prop.id}>
                          {prop.title}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-white/60 mt-2">
                      Link this post to a property in our catalog for easy navigation
                    </p>
                  </div>
                )}

                {source === 'external' && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                    <label className="block text-sm font-medium mb-2 text-white/90">
                      External Listing URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={externalUrl}
                      onChange={(e) => setExternalUrl(e.target.value)}
                      placeholder="https://example.com/property/123"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500/50 focus:bg-white/10 transition-all"
                    />
                    <p className="text-xs text-white/60 mt-2">
                      Add a link to your external property listing
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-medium transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#FDB022] hover:bg-[#FDB022]/90 text-black rounded-xl font-medium transition-all"
                  >
                    Post Stock
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}